import { type ReactNode, useEffect, useId, useRef } from "react";
import { XIcon } from "./icons";
import { cn, focusRing } from "./utils";

export function useEscape(active: boolean, onEscape: () => void) {
  const handler = useRef(onEscape);
  handler.current = onEscape;
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handler.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [active]);
}

// Moves focus into an overlay when it opens and back to where it was when it closes.
export function useFocusReturn<T extends HTMLElement>(active: boolean) {
  const ref = useRef<T>(null);
  useEffect(() => {
    if (!active) return;
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    return () => previous?.focus?.();
  }, [active]);
  return ref;
}

export function Scrim({ onClick, className }: { onClick?: () => void; className?: string }) {
  return (
    // The scrim is a pointer shortcut only. Keyboard users close with Escape or the close button.
    <div
      aria-hidden="true"
      onClick={onClick}
      className={cn("absolute inset-0 bg-scrim backdrop-blur-[3px]", className)}
    />
  );
}

export function CloseButton({
  onClick,
  label = "Close",
  size = 40,
}: {
  onClick: () => void;
  label?: string;
  size?: number;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      style={{ width: size, height: size }}
      className={cn(
        "flex flex-none cursor-pointer items-center justify-center rounded-full bg-surface-header text-ink hover:bg-surface-low",
        focusRing,
      )}
    >
      <XIcon size={17} strokeWidth={2.4} />
    </button>
  );
}

export type SheetProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  leading?: ReactNode;
  children: ReactNode;
  /** Fill the viewport. Off to fill the nearest positioned parent, such as a conversation pane. */
  fixed?: boolean;
  className?: string;
};

// A bottom sheet on phones. From 900px wide it becomes a centred dialog.
export function Sheet({
  open,
  onClose,
  title,
  subtitle,
  leading,
  children,
  fixed = true,
  className,
}: SheetProps) {
  const titleId = useId();
  const ref = useFocusReturn<HTMLElement>(open);
  useEscape(open, onClose);
  if (!open) return null;
  return (
    <div
      className={cn(
        fixed ? "fixed" : "absolute",
        "inset-0 z-40 flex items-end justify-center min-[900px]:items-center",
      )}
    >
      <Scrim onClick={onClose} />
      <section
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          "relative max-h-[92%] w-full overflow-y-auto rounded-t-sheet bg-linen px-[18px] pt-2.5 pb-[max(26px,env(safe-area-inset-bottom))] outline-none",
          "min-[900px]:max-w-[440px] min-[900px]:rounded-sheet min-[900px]:pt-5 min-[900px]:shadow-menu",
          className,
        )}
      >
        <div className="mx-auto mb-4 h-[5px] w-10 rounded-[5px] bg-line-strong min-[900px]:hidden" />
        <div className="flex items-center gap-3">
          {leading}
          <span className="flex min-w-0 flex-1 flex-col">
            <h2
              id={titleId}
              className={cn(
                "m-0 font-serif font-medium tracking-[-0.01em]",
                subtitle ? "text-2xl" : "text-[26px]",
              )}
            >
              {title}
            </h2>
            {subtitle && <span className="text-[13px] text-ink-secondary">{subtitle}</span>}
          </span>
          <CloseButton onClick={onClose} />
        </div>
        {children}
      </section>
    </div>
  );
}

export function Toast({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div role="status" className={cn("rounded-full bg-blue px-4 py-2.5 text-[13px] text-white", className)}>
      {children}
    </div>
  );
}
